import Konva from 'konva';
import { getSharedStage } from '../components/canvas/stage/Stage';
import { useProjectState, setProjectState, getProjectState } from './project';

const SCALE_BY = 1.2;
const MAX_SCALE = 2;
const MIN_SCALE = 0.02;
const FIT_VIEW_PADDING = 78;

export const useViewport = () => {
  return useProjectState('viewport');
};

export const getViewport = () => {
  return getProjectState('viewport');
};

export const setViewport = (partialState) => {
  const viewport = getViewport();
  setProjectState(
    { viewport: { ...viewport, ...partialState } },
    {
      __skipTracking: true,
    },
  );
};

const fitView = (options = {}) => {
  const elements = options.elements || getProjectState('elements');

  if (!elements.length) return;

  const [canvasWidth, canvasHeight] = getProjectState('canvasSize');
  const animate = typeof options.animate === 'boolean' ? options.animate : elements.length < 20;

  const [left, top, right, bottom] = elements.reduce(
    (acc, item) => {
      if (item.type === 'group') {
        item.elements.forEach((child) => {
          acc[0] = Math.min(acc[0], child.x);
          acc[1] = Math.min(acc[1], child.y);
          acc[2] = Math.max(acc[2], child.x + child.width * child.scaleX);
          acc[3] = Math.max(acc[3], child.y + child.height * child.scaleY);
        });
      } else {
        acc[0] = Math.min(acc[0], item.x);
        acc[1] = Math.min(acc[1], item.y);
        acc[2] = Math.max(acc[2], item.x + item.width * item.scaleX);
        acc[3] = Math.max(acc[3], item.y + item.height * item.scaleY);
      }
      return acc;
    },
    [Infinity, Infinity, 0, 0],
  );

  const width = right - left;
  const height = bottom - top;

  const nextScaleX = (canvasWidth - FIT_VIEW_PADDING * 2) / width;
  const nextScaleY = (canvasHeight - FIT_VIEW_PADDING * 2) / height;
  const nextScale = Math.max(options.minScale || 0, Math.min(nextScaleX, nextScaleY));
  const nextX = (canvasWidth - width * nextScale) / 2 - left * nextScale;
  const nextY = (canvasHeight - height * nextScale) / 2 - top * nextScale;

  if (animate) {
    const stage = getSharedStage();
    if (!stage) return;

    stage.to({
      x: nextX,
      y: nextY,
      scaleX: nextScale,
      scaleY: nextScale,
      duration: 0.3,
      easing: Konva.Easings.EaseInOut,
      onUpdate: () => {
        setViewport({
          x: stage.x(),
          y: stage.y(),
          scale: stage.scaleX(),
        });
      },
      onFinish: () => {
        setViewport({ x: nextX, y: nextY, scale: nextScale });
      },
    });
  } else {
    setViewport({ x: nextX, y: nextY, scale: nextScale });
  }
};

const locateRect = (rect, options) => {
  const [canvasWidth, canvasHeight] = getProjectState('canvasSize');
  const { keepScale = true, fitView = false, targetScale, animate, paddings = [100, 100, 200, 100] } = options || {};

  const { scale } = getViewport();
  const { x, y, width, height } = rect;

  let nextX, nextY, nextScale;

  if (targetScale) {
    nextX = x * -1 * targetScale + (canvasWidth - width * targetScale) / 2;
    nextY = y * -1 * targetScale + (canvasHeight - height * targetScale) / 2;
    nextScale = targetScale;
  } else if (fitView) {
    const [top, right, bottom, left] = paddings;

    const nextWidth = width + left + right;
    const nextHeight = height + top + bottom;
    const nextScaleX = (canvasWidth - right - left) / nextWidth;
    const nextScaleY = (canvasHeight - bottom - top) / nextHeight;

    nextScale = Math.min(nextScaleX, nextScaleY);

    nextX = x * -1 * nextScale + (canvasWidth - nextWidth * nextScale) / 2 + left * nextScale;
    nextY = y * -1 * nextScale + (canvasHeight - nextHeight * nextScale) / 2 + top * nextScale;
  } else if (!keepScale) {
    nextX = (canvasWidth - width) / 2 - x;
    nextY = 100 - y;
    nextScale = 1;
  } else {
    nextX = x * -1 * scale + (canvasWidth - width * scale) / 2;
    nextY = y * -1 * scale + (canvasHeight - height * scale) / 2;
    nextScale = scale;
  }

  if (animate) {
    const stage = getSharedStage();
    if (!stage) return;

    stage.to({
      x: nextX,
      y: nextY,
      scaleX: nextScale,
      scaleY: nextScale,
      duration: 0.3,
      easing: Konva.Easings.EaseInOut,
      onUpdate: () => {
        setViewport({
          x: stage.x(),
          y: stage.y(),
          scale: stage.scaleX(),
        });
      },
      onFinish: () => {
        setViewport({ x: nextX, y: nextY, scale: nextScale });
      },
    });
  } else {
    setViewport({ x: nextX, y: nextY, scale: nextScale });
  }
};

const locateElement = (elementId, options) => {
  const [canvasWidth, canvasHeight] = getProjectState('canvasSize');
  const { keepScale = true, fitView = false, targetScale, animate, paddings = [100, 100, 200, 100] } = options || {};
  const element = getProjectState('elements').find((item) => item.id === elementId);

  if (!element) return;

  const { scale } = getViewport();
  const { x, y, width, height, scaleX, scaleY } = element;

  let nextX, nextY, nextScale;

  if (targetScale) {
    nextX = x * -1 * targetScale + (canvasWidth - width * scaleX * targetScale) / 2;
    nextY = y * -1 * targetScale + (canvasHeight - height * scaleY * targetScale) / 2;
    nextScale = targetScale;
  } else if (fitView) {
    const [top, right, bottom, left] = paddings;

    const nextWidth = width * scaleX + left + right;
    const nextHeight = height * scaleY + top + bottom;

    const nextScaleX = (canvasWidth - right - left) / nextWidth;
    const nextScaleY = (canvasHeight - bottom - top) / nextHeight;
    const minScale = 0.25;
    const maxScale = 2;

    nextScale = Math.min(nextScaleX, nextScaleY);
    nextScale = Math.max(minScale, Math.min(nextScale, maxScale));

    nextX = x * -1 * nextScale + (canvasWidth - nextWidth * nextScale) / 2 + left * nextScale;
    nextY = y * -1 * nextScale + (canvasHeight - nextHeight * nextScale) / 2 + top * nextScale;
  } else if (!keepScale) {
    nextX = (canvasWidth - width * scaleX) / 2 - x;
    nextY = 100 - y;
    nextScale = 1;
  } else {
    nextX = x * -1 * scale + (canvasWidth - width * scaleX * scale) / 2;
    nextY = y * -1 * scale + (canvasHeight - height * scaleY * scale) / 2;
    nextScale = scale;
  }

  if (animate) {
    const stage = getSharedStage();
    if (!stage) return;

    stage.to({
      x: nextX,
      y: nextY,
      scaleX: nextScale,
      scaleY: nextScale,
      duration: 0.3,
      easing: Konva.Easings.EaseInOut,
      onUpdate: () => {
        setViewport({
          x: stage.x(),
          y: stage.y(),
          scale: stage.scaleX(),
        });
      },
      onFinish: () => {
        setViewport({ x: nextX, y: nextY, scale: nextScale });
      },
    });
  } else {
    setViewport({ x: nextX, y: nextY, scale: nextScale });
  }
};

const zoomIn = (origin) => {
  typeof origin !== 'string' && (origin = 'pointer');

  let pointer, nextX, nextY, nextScale;
  const stage = getSharedStage();
  const prevScale = stage.scaleX();

  if (origin === 'pointer') {
    pointer = stage.getPointerPosition();
  } else if (origin === 'screen-center') {
    const [canvasWidth, canvasHeight] = getProjectState('canvasSize');
    pointer = { x: canvasWidth / 2, y: canvasHeight / 2 };
  }

  const originPoint = {
    x: (pointer.x - stage.x()) / prevScale,
    y: (pointer.y - stage.y()) / prevScale,
  };

  nextScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prevScale * SCALE_BY));
  nextX = pointer.x - originPoint.x * nextScale;
  nextY = pointer.y - originPoint.y * nextScale;

  setViewport({
    x: nextX,
    y: nextY,
    scale: nextScale,
  });
};

const zoomOut = (origin) => {
  typeof origin !== 'string' && (origin = 'pointer');

  let pointer, nextX, nextY, nextScale;
  const stage = getSharedStage();
  const prevScale = stage.scaleX();

  if (origin === 'pointer') {
    pointer = stage.getPointerPosition();
  } else if (origin === 'screen-center') {
    const [canvasWidth, canvasHeight] = getProjectState('canvasSize');
    pointer = { x: canvasWidth / 2, y: canvasHeight / 2 };
  }

  const originPoint = {
    x: (pointer.x - stage.x()) / prevScale,
    y: (pointer.y - stage.y()) / prevScale,
  };

  nextScale = Math.max(MIN_SCALE, prevScale / SCALE_BY);
  nextX = pointer.x - originPoint.x * nextScale;
  nextY = pointer.y - originPoint.y * nextScale;

  setViewport({
    x: nextX,
    y: nextY,
    scale: nextScale,
  });
};

const zoomTo = (nextScale, origin) => {
  typeof origin !== 'string' && (origin = 'screen-center');

  let pointer, nextX, nextY;
  const stage = getSharedStage();
  const prevScale = stage.scaleX();

  if (origin === 'pointer') {
    pointer = stage.getPointerPosition();
  } else if (origin === 'screen-center') {
    const [canvasWidth, canvasHeight] = getProjectState('canvasSize');
    pointer = { x: canvasWidth / 2, y: canvasHeight / 2 };
  }

  const originPoint = {
    x: (pointer.x - stage.x()) / prevScale,
    y: (pointer.y - stage.y()) / prevScale,
  };

  nextX = pointer.x - originPoint.x * nextScale;
  nextY = pointer.y - originPoint.y * nextScale;

  setViewport({
    x: nextX,
    y: nextY,
    scale: nextScale,
  });
};

const zoomReset = (origin) => {
  typeof origin !== 'string' && (origin = 'screen-center');

  let pointer, nextX, nextY;
  const stage = getSharedStage();
  const prevScale = stage.scaleX();

  if (origin === 'pointer') {
    pointer = stage.getPointerPosition();
  } else if (origin === 'screen-center') {
    const [canvasWidth, canvasHeight] = getProjectState('canvasSize');
    pointer = { x: canvasWidth / 2, y: canvasHeight / 2 };
  }

  const originPoint = {
    x: (pointer.x - stage.x()) / prevScale,
    y: (pointer.y - stage.y()) / prevScale,
  };

  nextX = pointer.x - originPoint.x;
  nextY = pointer.y - originPoint.y;

  setViewport({
    x: nextX,
    y: nextY,
    scale: 1,
  });
};

export const viewportActions = {
  fitView,
  locateRect,
  locateElement,
  zoomIn,
  zoomOut,
  zoomTo,
  zoomReset,
};
